import * as df from 'date-fns';
import { remove } from 'lodash-es';
import { InjectedIntl } from 'react-intl';

import * as IBooks from '../../../../interfaces/merchants/resources/books';
import Regulation from '../../../../domain/entities/regulation/Regulation';
import ZoomSetting from '../../../../domain/entities/ZoomSetting';
import ReservableRegulation from '../../../../domain/entities/regulation/ReservableRegulation';
import CancelableRegulation from '../../../../domain/entities/regulation/CancelableRegulation';
import ChangeableRegulation from '../../../../domain/entities/regulation/ChangeableRegulation';

// TODO hogeByRegulation系のメソッドがBFF用 予約フローからV2参照無くせたらこっちに統一できるはず

const finder = (name: string) =>
  name
    .replace(/\[(\w+)\]/g, '.$1')
    .replace(/^\./, '')
    .split('.');

const bindDown = (key: string, obj: object) => {
  let found = key in obj ? obj[key] : {};
  found = Array.isArray(found) ? Object.values({ ...found }) : { ...found };
  obj[key] = found;
  return found;
};

// TODO 実績があるのでここの実装が削除できるときまではこのままでいく(削除後にByRegulation側を要リファクタリング)
// https://github.com/coubic/frontend/pull/830#discussion_r539209840
const createReservablePeriod = (
  intl: InjectedIntl,
  r: IBooks.ReservableType,
): string => {
  if (!r) {
    return '';
  }
  if (r.type === 'monthly') {
    const monthsText =
      r.months === 0
        ? intl.formatMessage({ id: 'books.note.thisMonth' })
        : r.months === 1
        ? intl.formatMessage({ id: 'books.note.lastMonth' })
        : intl.formatMessage(
            { id: 'books.note.beforeMonths' },
            { months: r.months },
          );
    return intl.formatMessage(
      { id: 'books.note.reservablePeriod' },
      { months: monthsText, days: r.days, hours: r.hours, minutes: r.minutes },
    );
  }
  if (r.type === 'fixed_term_x_days') {
    return intl.formatMessage(
      { id: 'books.note.beforeDays' },
      { days: r.days },
    );
  }
  if (r.type === 'fixed_term_x_hours') {
    return intl.formatMessage(
      { id: 'books.note.beforeHours' },
      { hours: r.hours },
    );
  }
  throw new Error(`cannot create ReservablePeriod with ${r}`);
};

const createReservablePeriodByRegulation = (
  intl: InjectedIntl,
  r?: ReservableRegulation,
): string => {
  if (r === undefined || r.type === undefined) {
    return '';
  }

  if (r.type === 'monthly') {
    const monthsText =
      r.months === 0
        ? intl.formatMessage({ id: 'books.note.thisMonth' })
        : r.months === 1
        ? intl.formatMessage({ id: 'books.note.lastMonth' })
        : intl.formatMessage(
            { id: 'books.note.beforeMonths' },
            { months: r.months },
          );
    return intl.formatMessage(
      { id: 'books.note.reservablePeriod' },
      { months: monthsText, days: r.days, hours: r.hours, minutes: r.minutes },
    );
  }
  if (r.type === 'fixed_term_x_days') {
    return intl.formatMessage(
      { id: 'books.note.beforeDays' },
      { days: r.days },
    );
  }
  if (r.type === 'fixed_term_x_hours') {
    return intl.formatMessage(
      { id: 'books.note.beforeHours' },
      { hours: r.hours },
    );
  }
  throw new Error(`cannot create ReservablePeriod with ${r}`);
};

const createReservableFromNote = (
  intl: InjectedIntl,
  from: IBooks.ReservableType,
): Note | undefined => {
  const period = createReservablePeriod(intl, from);
  return period
    ? {
        title: intl.formatMessage({ id: 'books.note.reserveFrom.title' }),
        description: `${period}${intl.formatMessage({
          id: 'books.note.from',
        })}`,
      }
    : undefined;
};

const createReservableFromNoteByRegulation = (
  intl: InjectedIntl,
  from?: ReservableRegulation,
): Note | undefined => {
  const period = createReservablePeriodByRegulation(intl, from);
  return period
    ? {
        title: intl.formatMessage({ id: 'books.note.reserveFrom.title' }),
        description: `${period}${intl.formatMessage({
          id: 'books.note.from',
        })}`,
      }
    : undefined;
};

const createReservableUntilNote = (
  intl: InjectedIntl,
  until: IBooks.ReservableType,
): Note | undefined => {
  const period = createReservablePeriod(intl, until);
  return period
    ? {
        title: intl.formatMessage({ id: 'books.note.reserveUntil.title' }),
        description: `${period}${intl.formatMessage({
          id: 'books.note.until',
        })}`,
      }
    : undefined;
};

const createReservableUntilNoteByRegulation = (
  intl: InjectedIntl,
  until?: ReservableRegulation,
): Note | undefined => {
  const period = createReservablePeriodByRegulation(intl, until);
  return period
    ? {
        title: intl.formatMessage({ id: 'books.note.reserveUntil.title' }),
        description: `${period}${intl.formatMessage({
          id: 'books.note.until',
        })}`,
      }
    : undefined;
};

const createChangeableOrCancelablePeriod = (
  intl: InjectedIntl,
  c: IBooks.ChangeableType | IBooks.CancelableType,
): string => {
  if (!c || c.type !== 'accept') {
    throw new Error(`cannot create Period with ${c}`);
  }
  switch (true) {
    case c.until === 0: {
      return intl.formatMessage({ id: 'books.note.untilReserveDateTime' });
    }
    case c.until < 86400: {
      return intl.formatMessage(
        { id: 'books.note.untilReserveByHours' },
        { hours: c.until / 60 / 60 },
      );
    }
    default: {
      return intl.formatMessage(
        { id: 'books.note.untilReserveByDays' },
        { days: c.until / 60 / 60 / 24 },
      );
    }
  }
};

const createChangeableOrCancelablePeriodByRegulation = (
  intl: InjectedIntl,
  c: ChangeableRegulation | CancelableRegulation,
): string => {
  if (!c || c.type !== 'accept' || c.until === undefined) {
    throw new Error(`cannot create Period with ${c}`);
  }
  switch (true) {
    case c.until === 0: {
      return intl.formatMessage({ id: 'books.note.untilReserveDateTime' });
    }
    case c.until && c.until < 86400: {
      return intl.formatMessage(
        { id: 'books.note.untilReserveByHours' },
        { hours: c.until / 60 / 60 },
      );
    }
    default: {
      return intl.formatMessage(
        { id: 'books.note.untilReserveByDays' },
        { days: c.until / 60 / 60 / 24 },
      );
    }
  }
};

const createChangeableDescription = (
  intl: InjectedIntl,
  changeable: IBooks.ChangeableType,
): string => {
  if (!changeable) {
    return '';
  }
  if (changeable.type === 'not_accept') {
    return intl.formatMessage({ id: 'books.note.changeable.notAccept' });
  }
  if (changeable.type === 'accept') {
    return createChangeableOrCancelablePeriod(intl, changeable);
  }
  throw new Error(`cannot create ChangeableDescription with ${changeable}`);
};

const createChangeableDescriptionByRegulation = (
  intl: InjectedIntl,
  changeable?: ChangeableRegulation,
): string => {
  if (changeable === undefined || changeable.type === undefined) {
    return '';
  }
  if (changeable.type === 'not_accept') {
    return intl.formatMessage({ id: 'books.note.changeable.notAccept' });
  }
  if (changeable.type === 'accept') {
    return createChangeableOrCancelablePeriodByRegulation(intl, changeable);
  }
  throw new Error(`cannot create ChangeableDescription with ${changeable}`);
};

const createChangeableNote = (
  intl: InjectedIntl,
  changeable: IBooks.ChangeableType,
): Note | undefined => {
  const description = createChangeableDescription(intl, changeable);
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.changeable.title' }),
        description,
      }
    : undefined;
};

const createChangeableNoteByRegulation = (
  intl: InjectedIntl,
  changeable?: ChangeableRegulation,
): Note | undefined => {
  const description = createChangeableDescriptionByRegulation(intl, changeable);
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.changeable.title' }),
        description,
      }
    : undefined;
};

const createCancelableDescription = (
  intl: InjectedIntl,
  cancelable: IBooks.CancelableType,
): { description: string; caution?: string } => {
  if (!cancelable) {
    return { description: '' };
  }
  if (cancelable.type === 'not_accept') {
    return {
      description: intl.formatMessage({
        id: 'books.note.cancelable.notAccept',
      }),
    };
  }
  if (cancelable.type === 'accept') {
    const description = createChangeableOrCancelablePeriod(intl, cancelable);
    const caution = cancelable.prepayable
      ? intl.formatMessage(
          { id: 'books.note.cancelable.caution' },
          { days: cancelable.refundableDaysLimit },
        )
      : undefined;
    return { description, caution };
  }
  throw new Error(`cannot create ChangeableDescription with ${cancelable}`);
};

const createCancelableDescriptionByRegulation = (
  intl: InjectedIntl,
  cancelable: CancelableRegulation,
): { description: string; caution?: string } => {
  if (cancelable.type === undefined) {
    return { description: '' };
  }
  if (cancelable.type === 'not_accept') {
    return {
      description: intl.formatMessage({
        id: 'books.note.cancelable.notAccept',
      }),
    };
  }
  if (cancelable.type === 'accept') {
    const description = createChangeableOrCancelablePeriodByRegulation(
      intl,
      cancelable,
    );
    const caution = cancelable.prepayable
      ? intl.formatMessage(
          { id: 'books.note.cancelable.caution' },
          { days: cancelable.refundableDaysLimit },
        )
      : undefined;
    return { description, caution };
  }
  throw new Error(`cannot create ChangeableDescription with ${cancelable}`);
};

const createCancelableNote = (
  intl: InjectedIntl,
  cancelable: IBooks.CancelableType,
): Note | undefined => {
  const { description, caution } = createCancelableDescription(
    intl,
    cancelable,
  );
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.cancelable.title' }),
        description,
        caution,
      }
    : undefined;
};

const createCancelableNoteByRegulation = (
  intl: InjectedIntl,
  cancelable: CancelableRegulation,
): Note | undefined => {
  const { description, caution } = createCancelableDescriptionByRegulation(
    intl,
    cancelable,
  );
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.cancelable.title' }),
        description,
        caution,
      }
    : undefined;
};

const createOnlineMeetingDescription = (
  intl: InjectedIntl,
  onlineMeeting: IBooks.OnlineMeetingType,
): string => {
  if (!onlineMeeting || onlineMeeting.auto_create === 'disabled') {
    return '';
  }

  if (onlineMeeting.auto_create === 'enabled') {
    const description = intl.formatMessage({
      id: 'books.note.onlineMeeting.enabled',
    });
    return description;
  }
  throw new Error(`cannot create OnlineDescription with ${onlineMeeting}`);
};

const createOnlineMeetingDescriptionByRegulation = (
  intl: InjectedIntl,
  zoomMeeting: ZoomSetting,
): string => {
  if (!zoomMeeting.enableAutoCreate) {
    return '';
  }

  if (zoomMeeting.enableAutoCreate) {
    const description = intl.formatMessage({
      id: 'books.note.onlineMeeting.enabled',
    });
    return description;
  }
  throw new Error(`cannot create OnlineDescription with ${zoomMeeting}`);
};

const createOnlineMeetingNote = (
  intl: InjectedIntl,
  onlineMeeting: IBooks.OnlineMeetingType,
): Note | undefined => {
  const description = createOnlineMeetingDescription(intl, onlineMeeting);
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.onlineMeeting.title' }),
        description,
      }
    : undefined;
};

const createZoomMeetingNoteByRegulation = (
  intl: InjectedIntl,
  zoomMeeting: ZoomSetting,
): Note | undefined => {
  const description = createOnlineMeetingDescriptionByRegulation(
    intl,
    zoomMeeting,
  );
  return description
    ? {
        title: intl.formatMessage({ id: 'books.note.onlineMeeting.title' }),
        description,
      }
    : undefined;
};

export const changeObjectByNestedString = (
  obj: object,
  name: string,
  // tslint:disable-next-line: no-any
  value: any,
): object => {
  // nameのstringでvalueをupdateする。
  // nameは `me.firstname` とか `options[0].description` とかで指定できる
  const names = finder(name);
  const newObj = { ...obj };
  let found = newObj;
  for (let i = 0, n = names.length; i < n; ++i) {
    const key = names[i];
    if (i === n - 1) {
      // last
      found[key] = value;
      break;
    }
    found = bindDown(key, found);
    if (i === 0) {
      // first
      newObj[key] = found;
    }
  }
  return newObj;
};

// tslint:disable-next-line: no-any
export const getValueByNestedString = (obj: object, name: string): any => {
  const names = finder(name);
  let found = obj;
  for (let i = 0, n = names.length; i < n; ++i) {
    const key = names[i];
    if (i === n - 1) {
      // last
      return found[key];
    }
    found = key in found ? found[key] : {};
  }
  return undefined;
};

export const isRandomStaff = (s: IBooks.Staff): s is IBooks.RandomStaff =>
  s.id === '';

interface Note {
  title: string;
  description: string;
  caution?: string;
}

export const createNotes = (
  intl: InjectedIntl,
  {
    reservableFrom,
    reservableUntil,
    changeable,
    cancelable,
    onlineMeeting,
  }: IBooks.SettingConfirmation | IBooks.SettingCompletion,
): Note[] => {
  return [
    createReservableFromNote(intl, reservableFrom),
    createReservableUntilNote(intl, reservableUntil),
    createChangeableNote(intl, changeable),
    createCancelableNote(intl, cancelable),
    createOnlineMeetingNote(intl, onlineMeeting),
  ].filter(n => !!n) as Note[];
};

export const createNotesByRegulation = (
  intl: InjectedIntl,
  regulation: Regulation,
  zoomSetting: ZoomSetting,
): Note[] => {
  return [
    createReservableFromNoteByRegulation(intl, regulation.reservableFrom),
    createReservableUntilNoteByRegulation(intl, regulation.reservableUntil),
    createChangeableNoteByRegulation(intl, regulation.changeable),
    createCancelableNoteByRegulation(intl, regulation.cancelable),
    createZoomMeetingNoteByRegulation(intl, zoomSetting),
  ].filter(n => !!n) as Note[];
};

export function radioBehaviorByCheckbox<T extends { id: number | string }>(
  mode: 'single' | 'multiple',
  checked: boolean,
  elements: T[],
  newElement: T,
): T[] {
  if (mode === 'single') {
    return checked ? [newElement] : [];
  }

  checked
    ? elements.push(newElement)
    : remove(elements, e => e.id === newElement.id);
  return elements;
}

export const getCalendarDate = (date: Date) => {
  const startDate = df.startOfMonth(date);
  const endDate = df.lastDayOfMonth(date);
  const endDayCount = endDate.getDate();
  const lastMonthEndDate = df.addMonths(endDate, -1);
  const lastMonthendDayCount = lastMonthEndDate.getDate();
  const startDay = startDate.getDay();
  const previous = df.addMonths(date, -1);
  const next = df.addMonths(date, 1);
  let dayCount = 1;

  const dates = [];
  for (let w = 0; w < 6; w++) {
    for (let d = 0; d < 7; d++) {
      let type = 'current';
      let stringDate = '';
      let datenum = 0;
      if (w === 0 && d < startDay) {
        // 1行目で1日の曜日の前
        datenum = lastMonthendDayCount - startDay + d + 1;
        stringDate = [
          previous.getFullYear(),
          `0${previous.getMonth() + 1}`.slice(-2),
          `0${datenum}`.slice(-2),
        ].join('-');
        type = 'previous';
      } else if (dayCount > endDayCount) {
        // 末尾の日数を超えた
        datenum = dayCount - endDayCount;
        stringDate = [
          next.getFullYear(),
          `0${next.getMonth() + 1}`.slice(-2),
          `0${datenum}`.slice(-2),
        ].join('-');
        type = 'next';
        dayCount++;
      } else {
        datenum = dayCount;
        stringDate = [
          date.getFullYear(),
          `0${date.getMonth() + 1}`.slice(-2),
          `0${dayCount}`.slice(-2),
        ].join('-');

        dayCount++;
      }
      dates.push({ date: datenum, iso: stringDate, type });
    }
  }
  return dates;
};
